import { v4 as uuidv4 } from "uuid";

/**
 * Rounds a number to the specified decimal places using arithmetic rounding.
 * Handles floating-point precision edge cases (e.g. 1.005 → 1.01) by shifting
 * the decimal point via string exponent notation rather than multiplying.
 *
 * BUG-2103: that string trick silently produced NaN for any value JavaScript
 * stringifies in exponential form — `1e-7 + "e2"` is the string `"1e-7e2"`,
 * which Number() cannot parse. This is reachable in the cart: a basket whose
 * lines net to zero (a sale and its return in the same cart, an exchange that
 * balances, a 100%-discounted cart) sums to a float residue like 5.55e-17, not
 * a clean 0. Before this guard that residue became a NaN cart.total — the same
 * "order syncs with a null total" class as BUG-2041/2044. The guard is applied
 * on both the shifted and unshifted values, since a large input can overflow
 * into exponential form only after the decimal shift (1e20 → "1e+22").
 *
 * Non-finite input (NaN/Infinity) is returned unchanged rather than coerced, so
 * an upstream NaN stays diagnosable instead of being laundered into a number.
 *
 * NOTE: Math.round breaks exact halves toward +Infinity, so this rounds
 * -22.925 to -22.92 while rounding 22.925 to 22.93. A return line therefore
 * does not always mirror its own sale line at an exact half. That asymmetry is
 * long-standing and is deliberately left unchanged here — see the BUG-2103
 * write-up before altering it, as ~122 call sites depend on current behaviour.
 */
export function roundTo(value, decimals = 2) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return numeric;

  const shifted = Number(`${numeric}e${decimals}`);
  if (!Number.isFinite(shifted)) {
    // Below half a minor unit → 0; too large to have a fractional part → unchanged.
    return Math.abs(numeric) < 1 ? 0 : numeric;
  }

  const restored = Number(`${Math.round(shifted)}e-${decimals}`);
  return Number.isFinite(restored) ? restored : numeric;
}

/**
 * Truncates a number to specified precision without rounding.
 * Returns a string with exactly `precision` decimal places.
 * Uses epsilon to avoid floating-point floor errors (e.g. 1.1 * 100 = 109.999...).
 */
export function truncateToPrecision(number, precision = 3) {
  const epsilon = 1e-10;
  const factor = Math.pow(10, precision);
  return (Math.trunc(number * factor + epsilon) / factor).toFixed(precision);
}

/**
 * Determines the effective tax-included flag from saleTypeData and tillData.
 * This logic is duplicated ~15 times across the codebase.
 */
export function getTaxIncludeFlag(saleTypeData, tillData) {
  if (saleTypeData?.customer?.istaxincluded === "Y") return "Y";
  return tillData?.tillAccess?.csBunit?.isTaxIncluded || "Y";
}

/**
 * Creates an empty cart object with fresh UUIDs.
 * Matches the initial state shape from Core/index.js.
 */
export function initializeCart(overrides = {}) {
  return {
    items: [],
    couponInput: [],
    advancePayment: 0,
    total: 0,
    tax: 0,
    discount: 0,
    paid: 0,
    change: 0,
    totalQty: 0,
    roundOff: 0,
    payments: [],
    redemptionPoints: 0,
    accumulationPoints: 0,
    creditAmount: 0,
    docType: "Invoice",
    sOrderID: uuidv4().replace(/-/g, "").toUpperCase(),
    referenceId: uuidv4().replace(/-/g, "").toUpperCase(),
    giftCardRefId: uuidv4().replace(/-/g, "").toUpperCase(),
    couponRefId: uuidv4().replace(/-/g, "").toUpperCase(),
    customer: null,
    salesRepId: null,
    cardPaymentData: {},
    documentno: "",
    userName: "",
    tillName: "",
    subTotal: 0,
    itemsSummery: {},
    amountInWords: "",
    groupedItemsByTaxRate: {},
    serviceCharge: 0,
    tip: 0,
    deiveryCharge: 0,
    packingCharge: 0,
    others: 0,
    refundAmount: 0,
    ...overrides,
  };
}

/**
 * Calculates tax amount for a given net total and tax rate.
 * @param {number} nettotal - Net total amount
 * @param {number} taxRate - Tax rate percentage
 * @param {string} taxIncludeFlag - "Y" if tax is included in price
 * @returns {number} Tax amount
 */
export function calculateTax(nettotal, taxRate, taxIncludeFlag = "Y") {
  if (!taxRate || taxRate === 0) return 0;
  if (taxIncludeFlag === "Y") {
    return nettotal - nettotal / (1 + taxRate / 100);
  }
  return (nettotal * taxRate) / 100;
}

/**
 * Gets the price precision from tillData.
 */
export function getPrecision(tillData) {
  return tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;
}

/**
 * Gets the rounding method from tillData currency config.
 * RU = Round Up, RD = Round Down, RN = Round Nearest, NR = No Rounding
 *
 * roundoffForPos gates this independently of the Backoffice rounding
 * config: "N" forces POS to skip rounding even when Backoffice/NetSuite
 * still round, per TSK-3996.
 */
export function getRoundingMethod(tillData) {
  const currency = tillData?.tillAccess?.csBunit?.currencies?.[0];
  if (currency?.roundoffForPos === "N") return "NR";
  return currency?.roundingMethod || "NR";
}

/**
 * Formats an amount with the till's own currency symbol, decimal precision,
 * and symbol placement -- e.g. "₹1,234.50" or "1.500 KWD" depending on the
 * till's currency config. Several call sites across the app (Aggregator order
 * lists, purchase orders, coupon/cash-limit messages, discount displays) had
 * ₹ and .toFixed(2) hardcoded directly instead of reading this from tillData,
 * which is wrong on any non-INR till (this codebase already has GCC-currency
 * clients on KWD/BHD/OMR, 3-decimal currencies -- see BUG-2103). Mirrors the
 * `symbolRightSide === "N" ? currSymbol : ""` before the value / `"Y"` after
 * convention already used inline in components like CartTotalsPanel.js, as
 * one shared helper instead of repeating it at every call site. Falls back to
 * ₹/2-decimals if a till's currency truly isn't resolvable yet (e.g. very
 * first render before login/till data has arrived).
 */
export function formatCurrencyAmount(tillData, amount) {
  const currency = tillData?.tillAccess?.csBunit?.currencies?.[0];
  const symbol = currency?.currSymbol || "₹";
  const precision = Number.isFinite(currency?.prcPrecision) ? currency.prcPrecision : 2;
  const value = Number(amount || 0).toFixed(precision);
  return currency?.symbolRightSide === "Y" ? `${value} ${symbol}` : `${symbol}${value}`;
}

/**
 * Just the till's currency symbol (e.g. "₹", "KWD"), falling back to ₹ if
 * unresolvable. For sites that already do their own number formatting
 * (locale grouping, custom precision) and only need the literal symbol
 * character made dynamic, not a full reformat via formatCurrencyAmount.
 */
export function getCurrencySymbol(tillData) {
  return tillData?.tillAccess?.csBunit?.currencies?.[0]?.currSymbol || "₹";
}

/**
 * Applies currency-based rounding to the gross total.
 * Returns the rounded total and the round-off difference.
 * roundOffAmount sign: negative = rounded down (customer pays less),
 *                      positive = rounded up (customer pays more).
 *                      Formula: roundedTotal - grossTotal
 *
 * BUG-2103: `grossTotal` arrives as a raw IEEE-754 accumulation of the line
 * totals (e.g. 22.93 + 73.80 === 96.72999999999999). Every branch below now
 * normalises it to the currency's own precision first:
 *
 *  - NR ("no rounding") means "do not round to the nearest currency UNIT" —
 *    it never meant "keep the binary noise". Returning grossTotal verbatim let
 *    that noise reach cart.total, where the payment gates compare it against a
 *    toFixed(precision) tendered amount and reject an exact-amount tender.
 *  - RU/RD had a latent sibling defect: Math.ceil(97.000000001) === 98.
 *    Normalising before ceil/floor removes that too.
 *
 * `precision` is REQUIRED — do not hard-code 2. Pass getPrecision(tillData) (or
 * the caller's own prcPrecision) so 3-decimal currencies (KWD/BHD/OMR) round to
 * fils, not to the nearest 0.01. Missing precision is reported loudly and falls
 * back to 2 rather than throwing: this runs inside the live cart pipeline, so a
 * throw here would take the whole till down. tests/unit/calculations/tillHelpers
 * asserts every call site passes the argument.
 *
 * @param {number} grossTotal - The unrounded gross total
 * @param {string} method - RU (ceil), RD (floor), RN (round), NR (none)
 * @param {number} precision - Currency price precision (prcPrecision)
 * @returns {{ roundedTotal: number, roundOffAmount: number }}
 */
export function applyCurrencyRounding(grossTotal, method = "NR", precision) {
  let effectivePrecision = Number(precision);
  if (!Number.isFinite(effectivePrecision)) {
    console.error("[BUG-2103] applyCurrencyRounding called without a valid precision — pass getPrecision(tillData). Falling back to 2.", { grossTotal, method, precision });
    effectivePrecision = 2;
  }

  // Normalise away float-accumulation noise before any branch decides anything.
  const normalisedGross = roundTo(grossTotal, effectivePrecision);

  if (!method || method === "NR") {
    return { roundedTotal: normalisedGross, roundOffAmount: 0 };
  }

  let roundedTotal;
  switch (method) {
    case "RU":
      roundedTotal = Math.ceil(normalisedGross);
      break;
    case "RD":
      roundedTotal = Math.floor(normalisedGross);
      break;
    case "RN":
      roundedTotal = Math.round(normalisedGross);
      break;
    default:
      return { roundedTotal: normalisedGross, roundOffAmount: 0 };
  }

  // Subtracting two clean values can still drift (97 - 96.73 = 0.27000000000000313),
  // and roundOff is written straight onto the cart/order, so normalise it too.
  const roundOffAmount = roundTo(roundedTotal - normalisedGross, effectivePrecision);
  return { roundedTotal, roundOffAmount };
}
